import 'devextreme/data/odata/store';
import { ColumnCellTemplateData } from 'devextreme/ui/data_grid';
import { ClickEvent } from 'devextreme/ui/button';

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { DxDataGridComponent } from 'devextreme-angular';

import { fieldNames } from 'app/Model/FieldNames';
import { Template } from 'app/Model/Catalog/Template';
import { TemplateItem } from 'app/Model/Catalog/TemplateItem';
import { NextPimField } from 'app/Model/Catalog/NextPimField';
import { ValidFeatureSystem } from 'app/Model/ValidFeatureSystem';
import { isFeatureField, isMimeField, isNextPimField } from 'app/Services/templateItemHelpers';
import { LanguageFlag } from 'app/Model/Dto/LanguageFlag';
import { WawiList } from 'app/Model/wawi/WawiList';

import { WarrantyClass } from 'app/Model/Catalog/WarrantyClass';
import { HazmatClass } from 'app/Model/Catalog/HazmatClass';
import { TemplateFieldsService } from 'app/Services/templateFields.service';
import { ItemClickEvent } from 'devextreme/ui/list';
import { ValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { SystemService } from '../../Services/system.service';
import { Functions } from '../../Services/functions';
import { LoginService } from '../../Services/login.service';
import { ValidFeatureService } from '../../Services/validFeature.service';

type DropdownFieldSource = { id: string; display: string; translate?: string[] };
type AutocompleteValues = { groupId: string; value: string };

@Component({
  selector: 'np-advanced-template-datagrid',
  templateUrl: './advanced-template-datagrid.component.html',
  standalone: false
})
export class AdvancedTemplateDatagridComponent implements OnInit {
  @Input() customerId: string;
  @Input() template: Template;
  @Output() templateChange = new EventEmitter<Template>();
  @Input() sheetIndex: number;
  @Input() wawiListMapping: WawiList[] = [new WawiList(null, '', null, null, null, null)];
  @Input() warrantyClasses: WarrantyClass[] = [];
  @Input() hazmatClasses: HazmatClass[] = [];
  @Input() languageFlags: LanguageFlag[] = [];
  @Input() validFeatureSystems: ValidFeatureSystem[] = [];
  @Output() onUpdate = new EventEmitter<Template>();

  dataSource: TemplateItem[] = [];

  // Das Popover wird in der registrierten Grid Zelle ausgelöst. Hier wird der State für die Darstellung
  // festgehalten und Elemente, die nicht mehr gebraucht werden, werden automatisch über den Garbage Collector
  // bereinigt.
  popoverView = new WeakMap<ColumnCellTemplateData<TemplateItem>, { target: HTMLElement; visible: boolean }>();

  // Die Property wird verwendet, um Seiteneffekte mit Kontrollelementen (wie z.B. ein drag handle)
  // zu berücksichtigen. Das ist speziell notwendig, da die Ebenen einfach gezählt werden und der
  // offset dann entsprechend mit einkalkuliert werden muss.
  columnOffset = 1;

  levelDepth = 1;
  get levels() {
    return Array(this.levelDepth)
      .fill(0)
      .map((_, index) => index);
  }

  levelChoices: AutocompleteValues[] = [];
  levelChoiceGroups: { [key: string]: string[] };

  typeFieldSource: DropdownFieldSource[] = [
    {
      id: 'standard',
      display: 'Produkt',
      translate: [
        fieldNames.descriptionShort,
        fieldNames.descriptionLong,
        fieldNames.descriptionLongWithoutHtml,
        fieldNames.manufacturerTypeDescription,
        fieldNames.remarks,
        fieldNames.manufacturerName
      ]
    },
    {
      id: 'mime',
      display: 'Medien',
      translate: [fieldNames.mimeAlt, fieldNames.mimeDescription]
    },
    {
      id: 'feature',
      display: 'Features',
      translate: [
        fieldNames.featureSystemGroupName,
        fieldNames.featureSystemGroupName,
        fieldNames.fName,
        fieldNames.fUnit,
        fieldNames.fValue
      ]
    },
    { id: 'orderdetail', display: 'OrderDetails' },
    { id: 'price', display: 'Preislisten' },
    { id: 'logistics', display: 'Logistik' },
    { id: 'reference', display: 'Referenzen' },
    { id: 'category', display: 'Kategorien' },
    { id: 'udx', display: 'UDX' },
    { id: 'seo', display: 'SEO - SEA' },
    { id: 'legal', display: 'Recht' },
    {
      id: 'wawi',
      display: this.loginService.wawiSettings?.name && this.loginService.wawiSettings?.name != '' ? this.loginService.wawiSettings?.name : 'Wawi'
    },
    { id: 'supplier', display: 'Lieferanten' },
    { id: 'shopware6', display: 'Shopware6', translate: [] },
    {
      id: 'packing',
      display: 'Verpackungen',
      translate: [fieldNames.packingUnit]
    },
    { id: 'fixed', display: 'Festwert' },
    { id: 'kiprompt', display: 'KI-Prompt', translate: [] },
    { id: 'scireum_sellsite', display: 'Scireum Sellsite' },
    { id: 'sellThroughFigures', display: 'Abverkaufsmengen' },
    { id: 'displayItems', display: 'Display-Artikel' },
  ];

  columnOrderList: { column: string; order: number }[] = [];

  
  @ViewChild(DxDataGridComponent) private grid: DxDataGridComponent;

  private featureSystemNameMap = new Map<string, string>();
  private featureNameMap = new Map<string, string>();

  constructor(
    public fieldService: TemplateFieldsService,
    public systemService: SystemService,
    private translate: TranslateService,
    private loginService: LoginService,
    private validFeatureService: ValidFeatureService
  ) {
    this.calculateCellValueField = this.calculateCellValueField.bind(this);
  }

  async ngOnInit() {
    this.levelChoiceGroups = {};

    for (const fieldSource of this.typeFieldSource) {
      if (fieldSource.display != 'UDX')
        fieldSource.display = this.translate.instant(fieldSource.display);
    }

    this.template.worksheetItems[this.sheetIndex].templateItems.forEach((x) => {
      x.guid = Functions.newGuid();
    });
    this.dataSource = this.template.worksheetItems[this.sheetIndex].templateItems;

    await this.fieldService.loadFields();
    this.handleLoadedTemplate();

    this.buildFeatureSystemNameMap();
    this.grid?.instance?.beginCustomLoading(this.translate.instant('Laden'));
    try {
      await this.loadSearchableFeatureContent();
    } finally {
      this.grid?.instance?.endCustomLoading();
    }
    this.grid?.instance?.refresh();
  }

  private buildFeatureSystemNameMap() {
    this.featureSystemNameMap.clear();
    for (const system of this.validFeatureSystems ?? []) {
      if (system.id) {
        this.featureSystemNameMap.set(system.id, system.name ?? '');
      }
    }
  }

  private async loadSearchableFeatureContent() {
    const systemKeys = new Set<string>();
    for (const item of this.dataSource) {
      for (const pimField of item.pimFields ?? []) {
        if (pimField.fieldType === 'feature' && pimField.systemKey && pimField.systemKey !== '-1') {
          systemKeys.add(pimField.systemKey);
        }
      }
    }

    await Promise.all(
      [...systemKeys].map(async (systemKey) => {
        try {
          const features = await lastValueFrom(
            this.validFeatureService.getFeatures(systemKey, this.customerId, false)
          );
          for (const feature of features ?? []) {
            if (feature.id) {
              this.featureNameMap.set(this.featureContentKey(systemKey, feature.id), feature.name ?? '');
            }
          }
        } catch {
          return;
        }
      })
    );
  }

  private featureContentKey(systemKey: string, elementKey: string): string {
    return `${systemKey}::${elementKey}`;
  }

  async onAddNewRow(event: DxButtonTypes.ClickEvent) {
    const item = new TemplateItem([new NextPimField('-1', '-1', fieldNames.supplierPid)], 1, fieldNames.supplierPid);
    item.isNewEditorVersion = true;
    item.languageCode = 'deu';
    item.calculation = '';
    item.numberformat = '';
    item.seperator = '';
    item.mapping = '';
    item.isSeparatorMode = false;
    item.section = 1;
    item.keys = [];
    item.order = this.dataSource.length + 1;

    this.dataSource.push(item);

    this.updateDataSource();
    this.updateColumnOrderList();
    this.updateTemplate();
    this.fixkeys(this.levelDepth);
  }

  onRowRemoving(event) {
    var index = this.dataSource.findIndex((x) => x.guid == event.data.guid);
    this.dataSource.splice(index, 1);
  }

  removeRow() {
    this.updateDataSource();
    this.updateColumnOrderList();
    this.updateTemplate();
  }

  removeTemplateItem(cellData: ColumnCellTemplateData<TemplateItem>) {
    const index = this.dataSource.findIndex((x) => x.guid === cellData.data.guid);
    if (index < 0) return;

    this.dataSource.splice(index, 1);
    this.updateDataSource();
    this.updateColumnOrderList();
    this.updateTemplate();
  }

  duplicateTemplateItem(cellData: ColumnCellTemplateData<TemplateItem>) {
    const source = cellData.data;
    const index = this.dataSource.findIndex((x) => x.guid === source.guid);
    if (index < 0) return;

    const clone = JSON.parse(JSON.stringify(source)) as TemplateItem;
    clone.guid = Functions.newGuid();
    clone.order = source.order + 0.5;

    this.dataSource.splice(index + 1, 0, clone);

    this.updateDataSource();
    this.updateColumnOrderList();
    this.updateTemplate();
  }

  onOpenFieldSettings(event: ClickEvent, cellData: ColumnCellTemplateData<TemplateItem>) {
    const target = event.element;
    const visible = true;
    this.popoverView.set(cellData, { target, visible });
  }

  onCloseFieldSettings(cellData: ColumnCellTemplateData<TemplateItem>) {
    this.popoverView.delete(cellData);
  }

  onTemplateItemChange(updatedItem: TemplateItem, cellData: ColumnCellTemplateData<TemplateItem>) {
    if (!updatedItem) return;

    if (cellData?.data?.guid === updatedItem.guid) {
      Object.assign(cellData.data, updatedItem);
      this.update(cellData);
      return;
    }

    const idx = this.dataSource.findIndex((x) => x.guid === updatedItem.guid);
    if (idx >= 0) {
      Object.assign(this.dataSource[idx], updatedItem);

      if (cellData?.data) {
        Object.assign(cellData.data, updatedItem);
      }

      this.update(cellData);
      return;
    }

    if (cellData?.data) {
      Object.assign(cellData.data, updatedItem);
      this.update(cellData);
    }
  }

  onLevelNameChanged(cellData: ColumnCellTemplateData<TemplateItem>, event: ValueChangedEvent) {
    if (!event.event) return;

    const index = cellData.columnIndex - this.columnOffset;
    const keys = cellData.data.keys;
    this.setLevelChoice(keys, event.value, index);
    this.update(cellData);
  }

  async onOrderItemChanged(event: ValueChangedEvent, cellData: ColumnCellTemplateData<TemplateItem>) {
    const oldOrder = event.previousValue;
    const newOrder = this.fieldService.excelColumnToNumber(event.value);
    cellData.data.order = oldOrder;

    if (newOrder !== oldOrder) {
      this.updateOrders(oldOrder, newOrder);
    }

    this.dataSource.sort((a, b) => a.order - b.order);
    this.update(cellData);
  }

  async onOrderItemClick(
    event: ItemClickEvent<{ order: number; column: string }>,
    cellData: ColumnCellTemplateData<TemplateItem>
  ) {
    const newOrder = event.itemData.order;
    const oldOrder = cellData.data.order;
    if (newOrder !== oldOrder) {
      this.updateOrders(oldOrder, newOrder);
    }

    this.dataSource.sort();
    this.update(cellData);
  }

  updateColumnOrderList() {
    this.columnOrderList = [];
    for (let i = 0; i < this.dataSource.length; i++) {
      this.columnOrderList.push({ order: i + 1, column: this.fieldService.numberToExcelColumn(i + 1) });
    }
  }

  update(cellData: ColumnCellTemplateData<TemplateItem>) {
    for (const pimField of cellData.data.pimFields) {
      this.sanitizePimField(pimField);  
    }
    
    this.template.columnCount = 0;
    for (const sheet of this.template.worksheetItems) {
      this.template.columnCount += sheet.templateItems.length;
    }

    this.updateDataSource();
    this.updateColumnOrderList();
    this.updateTemplate();
  }

  fixkeys(newLevelDepth: number) {
    for (const source of this.dataSource) {
      while (source.keys.length > newLevelDepth) {
        source.keys.pop();
      }
      while (source.keys.length < newLevelDepth) {
        source.keys.push('');
      }
    }
  }

  addColumn() {
    this.fixkeys(this.levelDepth + 1);
    this.levelDepth++;

    this.updateTemplate();
  }

  removeColumn() {
    if (this.dataSource.some((x) => x.keys[this.levelDepth - 1]?.trim())) {
      this.systemService.notifyInfo('Es gibt noch gefüllte Überschriften auf dieser Ebene');
      return;
    }
    this.fixkeys(this.levelDepth - 1);
    this.levelDepth--;

    //if (this.levelChoices[this.levelDepth]) this.levelChoices.pop();

    this.updateTemplate();
  }

  hasTemplateItemSettings(templateItem: TemplateItem) {
    if (!templateItem) return false;
    const hasCalculation = templateItem.calculation !== null && templateItem.calculation !== '';
    const hasNumberFormat = templateItem.numberformat !== null && templateItem.numberformat !== '';
    const hasDateFormat = !!templateItem.dateformat;
    const hasSeparator = templateItem.seperator !== null && templateItem.seperator !== '';
    const hasMapping = templateItem.mapping !== null && templateItem.mapping !== '';
    const hasAutomaticSeparation = templateItem.maxCharacters > 0 && templateItem.section >= 1;
    const hasPrefixSuffix = templateItem.prefix?.trim() || templateItem.suffix?.trim();
    const hasFactor = templateItem.factorOperator && templateItem.calculation === 'FACTOR';
    return (
      templateItem.isFormulaMode ||
      hasCalculation ||
      hasNumberFormat ||
      hasDateFormat ||
      hasSeparator ||
      hasMapping ||
      hasAutomaticSeparation ||
      hasPrefixSuffix ||
      hasFactor
    );
  }

  isTranslatable(templateItem: TemplateItem) {
    const translatablePimFields = templateItem.pimFields?.filter((pf) => {
      const typeField = this.typeFieldSource.find((x) => x.id === pf.fieldType);
      return !typeField?.translate || typeField?.translate.includes(pf.field);
    });
    
    return translatablePimFields && translatablePimFields.length > 0;
  }

  private updateTemplate() {
    this.onUpdate.emit(this.template);
  }

  private updateDataSource() {
    const items = this.dataSource;
    items.sort((a, b) => a.order - b.order);
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      item.order = i + 1;
    }
  }

  private updateOrders(oldOrder: number = -1, newOrder: number = -1) {
    const items = this.dataSource;

    // Finde das zu aktualisierende Item
    const updatedItem = items.find((item) => item.order === oldOrder);
    if (!updatedItem) return;

    if (newOrder < 1) newOrder = 1;
    if (newOrder > items.length) newOrder = items.length;

    if (oldOrder < newOrder) {
      // Verschiebung nach hinten
      for (let item of items) {
        if (item.order > oldOrder && item.order <= newOrder) {
          item.order--;
        }
      }
    } else if (oldOrder > newOrder) {
      // Verschiebung nach vorne
      for (let item of items) {
        if (item.order >= newOrder && item.order < oldOrder) {
          item.order++;
        }
      }
    }

    // Setze die neue Order für das aktualisierte Item
    updatedItem.order = newOrder;
    this.updateDataSource();
  }

  private calcRealLevelDepth(items: TemplateItem[]) {
    return Math.max(1, ...items.map((x) => x.keys.length));
  }

  private setLevelChoice(keys: string[], value: string, index: number) {
    const groupId = keys.slice(0, index).join('|');
    if (!this.levelChoiceGroups[groupId]) {
      this.levelChoiceGroups[groupId] = [];
    }
    if (value && !this.levelChoiceGroups[groupId].includes(value)) {
      this.levelChoiceGroups[groupId].push(value);
    }
  }

  private sanitizePimField(pimField: NextPimField) {
    const field = pimField.field;
    if (
      ('feature' === pimField.fieldType && !isFeatureField(field)) ||
      ('mime' === pimField.fieldType && !isMimeField(field)) ||
      (!isNextPimField(field) && !this.fieldService.isKnownField(field))
    ) {
      pimField.elementKey = '-1';
      pimField.systemKey = '-1';
      pimField.field = '';
    }
  }

  private handleLoadedTemplate() {
    const items = this.template.worksheetItems[this.sheetIndex].templateItems;
    for (const item of items) {
      for (const pimField of item.pimFields ?? []) {
        pimField.fieldType = pimField.field ? this.fieldService.getFieldType(pimField) : (pimField.fieldType ?? 'standard');

        if ((pimField.field === fieldNames.benefits || pimField.field === fieldNames.topFeatures)
          && item.name && item.name !== '') {
          pimField.elementKey = item.name;
        }
      }
        
      if (item.key?.trim()) {
        item.keys.push(item.key);
        item.key = '';
      }

      for (let i = 0; i < item.keys.length; i++) {
        const value = item.keys[i];
        this.setLevelChoice(item.keys, value, i);
      }
    }
    this.levelDepth = this.calcRealLevelDepth(items);
    if (this.levelDepth === 0) {
      this.addColumn();
    }

    this.updateColumnOrderList();    
  }

  private typeFieldCache: { [key: string]: string } = {};

  calculateCellValueField(row: TemplateItem): string {
    if (!row) return '';

    let result = '';

    for (var pimField of row.pimFields) {
      if (pimField.value) {
        result += ' ' + pimField.value.toLowerCase();
      }

      let fieldname = pimField.field;
      if (!fieldname) continue;

      result += fieldname.toLowerCase();
      result += this.translate.instant(fieldname);

      if (pimField.fieldType) {
        result += pimField.fieldType.toLowerCase();
        let displayValue = this.typeFieldCache[pimField.fieldType];
        if (displayValue === undefined) {
          displayValue = this.typeFieldSource.find((x) => x.id === pimField.fieldType)?.display?.toLowerCase() ?? '';
          this.typeFieldCache[pimField.fieldType] = displayValue;
        }
        result += displayValue;
      }

      if (pimField.systemKey && pimField.systemKey !== '-1') {
        result += ' ' + (this.featureSystemNameMap.get(pimField.systemKey)?.toLowerCase() ?? '');

        if (pimField.elementKey && pimField.elementKey !== '-1') {
          result += ' ' + (this.featureNameMap.get(this.featureContentKey(pimField.systemKey, pimField.elementKey))?.toLowerCase() ?? '');
        }
      }
    }

    if (row.keys?.length) {
      result += ' ' + row.keys.join(' ').toLowerCase();
    }
    if (row.defaultValue) {
      result += ' ' + row.defaultValue.toLowerCase();
    }
    if (row.name) {
      result += ' ' + row.name.toLowerCase();
    }

    return result;
  }

  calculateCellValueOrder = (row: TemplateItem): string => {
    const orderNumber = row?.order ?? 0;
    if (!orderNumber) {
      return '';
    }
    const excelColumn = this.fieldService.numberToExcelColumn(orderNumber) || '';
    return [String(orderNumber), excelColumn, excelColumn.toLowerCase()].filter(Boolean).join(' | ');
  };

  calculateSortValueOrder(row:TemplateItem):number {
    return row?.order??0;
  }
  
  getKey(cellData: any): string | null {
    const idx = cellData.columnIndex - this.columnOffset;
    const keys = cellData?.data?.keys;
    return Array.isArray(keys) && idx >= 0 && idx < keys.length ? keys[idx] : null;
  }

  setKey(cellData: any, event: ValueChangedEvent): void {
    const idx = cellData.columnIndex - this.columnOffset;
    if (!cellData?.data) return;

    if (!Array.isArray(cellData.data.keys)) cellData.data.keys = [];

    if(cellData.data.keys.length == 0){
      this.fixkeys(this.levelDepth);
    }
    if (idx < 0 || idx >= cellData.data.keys.length) return;

    if (cellData.data.keys[idx] === event?.value)
      return;

    cellData.data.keys[idx] = event?.value ?? '';

    this.onLevelNameChanged(cellData, event)
  }
}
